import { SubmitStatus, SubmitToCourtError, } from '@debbie/common/dist/court/auto-submit.js';
import { GLcourts } from '@debbie/common/dist/court/countries/greenland.js';
import { getDirector } from '@debbie/common/dist/court/index.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import { VoucherCategory } from '@debbie/common/dist/economy/vouchers/index.js';
import { Entity } from '@debbie/common/dist/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { getMentionSuggestions, toEditorMentions, } from '@debbie/common/dist/templates/suggestions.js';
import { GLTribunalCaseType } from '@debbie/common/dist/tribunal/cases/countries/greenland.js';
import { GLTribunalJobType, defaultGLStaevningSagsfremstilling, toPDFData, } from '@debbie/common/dist/tribunal/jobs/countries/greenland.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { isBlankHtml } from '@debbie/common/dist/util/html.js';
import { safeParse } from '@debbie/common/dist/util/json.js';
import * as tribunalAPI from '@debbie/cortex/dist/api/tribunal.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { useReadyToSubmit } from '@debbie/cortex/dist/heavy-calculations/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js';
import { usePropertyTypes } from '@debbie/cortex/dist/properties/index.js';
import { useTemplates } from '@debbie/cortex/dist/templates/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { z } from 'zod/v4';
import { FILEBASE } from '../../../../config.js';
import { encodeText, parseText } from '../../../../utils/templates.js';
import Alert from '../../../Alert.js';
import Button from '../../../Button.js';
import ButtonGroup from '../../../ButtonGroup.js';
import Checkbox from '../../../Checkbox.js';
import DateInput from '../../../DateInput.js';
import NewEditor from '../../../editor/Editor.js';
import Errors from '../../../Errors.js';
import { RadioSelect } from '../../../index.js';
import Input from '../../../Input.js';
import { PDFTemplatePreview } from '../../../interactions/PDFTemplatePreview.js';
import Modal from '../../../Modal.js';
import MoneyInput from '../../../MoneyInput.js';
import ObjectComp from '../../../ObjectComp.js';
import EconomySettings from '../common/EconomySettings.js';
var PreviewPDF;
(function (PreviewPDF) {
    PreviewPDF[PreviewPDF["St\u00E6vning"] = 0] = "St\u00E6vning";
    PreviewPDF[PreviewPDF["Kvittering"] = 1] = "Kvittering";
    PreviewPDF[PreviewPDF["Posteringsbilag"] = 2] = "Posteringsbilag";
})(PreviewPDF || (PreviewPDF = {}));
const RememberedSettingsSchema = z
    .object({
    processInterest: z.boolean(),
    sendInfoToEconomyResponsible: z.boolean(),
    addCourtFeeToCase: z.boolean(),
    addCourtDisbursementToClientBilling: z.boolean(),
    addCourtFeeToClientBilling: z.boolean(),
})
    .partial();
const rememberedSettingsKey = (tenantId) => `staevning.${tenantId}`;
const getRememberedSettings = (tenantId) => {
    const remembered = safeParse(localStorage.getItem(rememberedSettingsKey(tenantId)) ?? '{}', RememberedSettingsSchema);
    return remembered.ok ? remembered.data : {};
};
const rememberSettings = (tenantId, settings) => {
    try {
        localStorage.setItem(rememberedSettingsKey(tenantId), JSON.stringify(RememberedSettingsSchema.parse({
            ...getRememberedSettings(tenantId),
            ...settings,
        })));
    }
    catch {
        // Remembered settings are a convenience; ignore storage failures
    }
};
const Staevning = (props) => {
    const lang = useSelector((s) => s.lang);
    const singleCaseLoader = useCase(props.caseId);
    const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null;
    const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId);
    const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null;
    const propertyTypesLoadable = usePropertyTypes();
    const propertyTypes = h.hasData(propertyTypesLoadable)
        ? propertyTypesLoadable.data
        : undefined;
    const tenant = useTenant();
    const mentionSuggestions = getMentionSuggestions(lang, TemplateType.LETTER, propertyTypes || undefined, tenant);
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const customerLoader = useCustomer(singleCase?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const caseInteractionsLoadable = useInteractionsByReference(Entity.Case, singleCase?.caseId);
    const caseInteractions = (h.hasData(caseInteractionsLoadable)
        ? caseInteractionsLoadable.data
        : []) || [];
    const updatesLoadable = useUpdatesByRefId(singleCase?.caseId);
    const caseUpdates = h.hasData(updatesLoadable) ? updatesLoadable.data : null;
    const documentsLoadable = useDocumentsByReference(singleCase?.caseId);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const templatesLoadable = useTemplates();
    const templates = h.hasData(templatesLoadable) ? templatesLoadable.data : null;
    const director = customer && getDirector(customer.users);
    const [previewPDF, setPreviewPDF] = useState(PreviewPDF.Stævning);
    const [data, setData] = useState(props.prefill
        ? {
            ...props.prefill,
            reasonHtml: encodeText(props.prefill.reasoning, mentionSuggestions),
            createdDate: moment(props.prefill.createdDate),
        }
        : {
            interestDate: moment().format('LL'),
            interests: 0,
            totalAmount: 0,
            processInterest: true,
            createdDate: moment(),
            courtFee: 50000,
            reasonHtml: '',
            lawyer: localStorage.staevningLawyer ?? '',
            paralegal: localStorage.staevningParalegal ?? '',
            sendInfoToEconomyResponsible: true,
            economyResponsibleUserId: localStorage.economyResponsibleUserId ?? null,
            addCourtFeeToCase: true,
            addCourtDisbursementToClientBilling: false,
            addCourtFeeToClientBilling: false,
            ...getRememberedSettings(props.tenantId),
        });
    const [initialReason, setInitialReason] = useState(props.prefill != null ? data.reasonHtml : null);
    if (initialReason == null &&
        creditor != null &&
        tenant != null &&
        propertyTypes != null) {
        const sagsfremstilling = [
            creditor.settings.courtSubmitConfig?.sagsfremstilling,
            tenant.settings.tribunal?.caseTypes?.[GLTribunalCaseType.GLStaevning]
                ?.sagsfremstilling,
        ].find((html) => html != null && !isBlankHtml(html)) ??
            defaultGLStaevningSagsfremstilling;
        setInitialReason(encodeText(sagsfremstilling, mentionSuggestions));
    }
    const [manuallyEditedAmount, setManuallyEditedAmount] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const getTotalAmount = (caseVouchers, addCourtFeeToCase) => {
        const totalAmount = -helpers.overview.balance(vouchers) +
            (addCourtFeeToCase ? data.courtFee : 0);
        return totalAmount;
    };
    useEffect(() => {
        if (caseVouchers != null && creditor != null) {
            const rawVouchers = caseVouchers;
            const vouchers = helpers.vouchers.postProcessing(rawVouchers, {
                includeDeletedVouchers: false,
                includeExpiredVouchers: false,
                includeIrrecoverableVouchers: false,
            });
            const totalAmount = getTotalAmount(caseVouchers, data.addCourtFeeToCase);
            const interests = -helpers.overview.balanceCategory(vouchers, VoucherCategory.INTEREST);
            // Written-off interest is not claimed.
            const interestStart = vouchers
                .filter(helpers.vouchers.isListed)
                .filter((v) => v.category === VoucherCategory.INTEREST)
                .map((v) => new Date(v.date))
                .sort()
                .map((date) => moment(date).format('LL'))[0];
            setData((s) => {
                const updateObj = props.prefill
                    ? {
                        interests: interests,
                        totalAmount: totalAmount,
                    }
                    : {
                        interestDate: interestStart ?? s.interestDate,
                        interests: interests,
                        totalAmount: totalAmount,
                        sendInfoToEconomyResponsible: creditor?.settings.courtSubmitConfig
                            ?.sendInfoToEconomyResponsible ??
                            s.sendInfoToEconomyResponsible,
                        economyResponsibleUserId: creditor?.settings.courtSubmitConfig
                            ?.economyResponsibleUserId ?? s.economyResponsibleUserId,
                        addCourtDisbursementToClientBilling: creditor?.settings.courtSubmitConfig
                            ?.addCourtDisbursementToClientBilling ??
                            s.addCourtDisbursementToClientBilling,
                        addCourtFeeToClientBilling: creditor?.settings.courtSubmitConfig
                            ?.addCourtFeeToClientBilling ?? s.addCourtFeeToClientBilling,
                    };
                return {
                    ...s,
                    ...updateObj,
                };
            });
        }
    }, [caseVouchers, creditor]);
    const vouchers = caseVouchers != null
        ? helpers.vouchers.postProcessing(caseVouchers, {
            includeDeletedVouchers: false,
            includeExpiredVouchers: false,
            includeIrrecoverableVouchers: false,
        })
        : [];
    const getData = () => {
        return {
            interestDate: data.interestDate,
            interests: data.interests,
            totalAmount: data.totalAmount,
            processInterest: data.processInterest,
            createdDate: data.createdDate.format('LL'),
            courtFee: data.courtFee,
            reasoning: parseText(data.reasonHtml, mentionSuggestions),
            lawyer: data.lawyer,
            paralegal: data.paralegal,
            director: director,
            courtId: GLcourts[0].courtId,
            // Invoices of written-off debt are not part of the claim.
            invoiceFileIds: vouchers
                .filter((v) => v.appendix != null && helpers.vouchers.isListed(v))
                .map((v) => v.appendix.fileId),
            sendInfoToEconomyResponsible: data.sendInfoToEconomyResponsible,
            economyResponsibleUserId: data.economyResponsibleUserId,
            addCourtFeeToCase: data.addCourtFeeToCase,
            addCourtDisbursementToClientBilling: data.addCourtDisbursementToClientBilling,
            addCourtFeeToClientBilling: data.addCourtFeeToClientBilling,
            isGreenland: true,
        };
    };
    const submitter = useMutation(async () => {
        if (!singleCase) {
            throw Error('Case not loaded');
        }
        const data = getData();
        await tribunalAPI.createTribunalJob({
            type: props.resending
                ? GLTribunalJobType.ResendStaevning
                : GLTribunalJobType.CreateStaevning,
            tribunalCaseId: props.tribunalCaseId,
            data: data,
            caseId: singleCase.caseId,
            reference: null,
        });
    }, {
        onSuccess: () => {
            if (props.onCreated) {
                props.onCreated();
            }
        },
    });
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const conditionsLoader = useCaseConditions(props.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const readyToSubmitLoader = useReadyToSubmit({
        case: singleCase,
        creditor,
        caseVouchers,
        customer,
        documents,
        interactions: caseInteractions,
        updates: caseUpdates,
        templates,
        tenant,
        fixedDistributions,
        conditions,
    }, {
        ...creditor?.settings.courtSubmitConfig,
        includedRules: [
            SubmitToCourtError.CaseHasDeposits,
            SubmitToCourtError.MoreThanOneDebtor,
            SubmitToCourtError.HasAlternativeContact,
            SubmitToCourtError.CurrencyNotDKK,
            SubmitToCourtError.ExcessiveCollectionCostImposed,
            SubmitToCourtError.ExcessiveCollectionFeeImposed,
            SubmitToCourtError.AlreadyInCourt,
            SubmitToCourtError.CaseHasExceededMaxDebtRegistrationsThreshold,
            SubmitToCourtError.ExcessivePrincipal,
        ],
    });
    const submittable = h.hasData(readyToSubmitLoader)
        ? readyToSubmitLoader.data
        : null;
    const canSubmit = initialReason != null &&
        data.lawyer != '' &&
        data.paralegal != '' &&
        // data.interestDate != '' &&
        (data.sendInfoToEconomyResponsible
            ? data.economyResponsibleUserId != null
            : true) &&
        submittable &&
        submittable.status !== SubmitStatus.No;
    return (React.createElement("div", null,
        h.isError(conditionsLoader) && (React.createElement(Alert, { message: lang.costCeiling.failedToLoad, size: "small" })),
        submittable &&
            submittable.errors.map((error) => (React.createElement(Alert, { message: lang.courtSubmitErrors[error], size: "small", key: error }))),
        React.createElement("h3", null, "Deltagere"),
        React.createElement(Input, { placeholder: "Advokat", onChange: (e) => {
                const val = e.currentTarget.value;
                setData((s) => ({ ...s, lawyer: val }));
                localStorage.setItem('staevningLawyer', val);
            }, value: data.lawyer }),
        React.createElement(Input, { placeholder: "Advokat fuldm\u00E6gtig", onChange: (e) => {
                const val = e.currentTarget.value;
                setData((s) => ({ ...s, paralegal: val }));
                localStorage.setItem('staevningParalegal', val);
            }, value: data.paralegal }),
        React.createElement("h3", null, "Rubrik 3"),
        React.createElement(MoneyInput, { placeholder: "Rentebel\u00F8b", onChange: (val) => {
                setData((s) => ({ ...s, interests: val }));
            }, value: data.interests }),
        React.createElement(MoneyInput, { placeholder: "Bel\u00F8b i alt", onChange: (val) => {
                setManuallyEditedAmount(true);
                setData((s) => ({ ...s, totalAmount: val }));
            }, value: data.totalAmount }),
        React.createElement(Checkbox, { placeholder: "Processrente", checked: data.processInterest, onChange: (e) => {
                const val = e.currentTarget.checked;
                setData((s) => ({ ...s, processInterest: val }));
                if (!props.prefill) {
                    rememberSettings(props.tenantId, { processInterest: val });
                }
            } }),
        h.isError(propertyTypesLoadable) && (React.createElement(Errors, { errors: [propertyTypesLoadable.error.message] })),
        initialReason != null && (React.createElement(NewEditor, { mentions: toEditorMentions(mentionSuggestions), onChange: (html) => {
                setData((s) => ({ ...s, reasonHtml: html }));
            }, initialContent: initialReason })),
        React.createElement("h3", null, "Rubrik 5"),
        React.createElement(EconomySettings, { customizableCourtFee: true, value: data, onChange: (es) => {
                if (!props.prefill) {
                    rememberSettings(props.tenantId, es);
                }
                if (es.addCourtFeeToCase != null &&
                    !manuallyEditedAmount &&
                    caseVouchers != null) {
                    setData((s) => ({
                        ...s,
                        ...es,
                        totalAmount: getTotalAmount(caseVouchers, es.addCourtFeeToCase),
                    }));
                }
                else {
                    setData((s) => ({ ...s, ...es }));
                }
            } }),
        React.createElement("h3", null, "Rubrik 6"),
        React.createElement(DateInput, { placeholder: "Oprettelsesdato", allowNull: false, value: data.createdDate, onChange: (d) => {
                setData((s) => ({ ...s, createdDate: d }));
            } }),
        submitter.isError && submitter.error instanceof Error && (React.createElement(Errors, { errors: [submitter.error.message] })),
        React.createElement("div", null,
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { size: "small", onClick: () => {
                        submitter.mutate();
                    }, disabled: !canSubmit, loading: submitter.isLoading }, "Send"),
                React.createElement(Button, { size: "small", theme: "secondary", disabled: !canSubmit, onClick: () => {
                        setPreviewModalOpen(true);
                    } }, "Forh\u00E5ndsvisning"))),
        previewModalOpen && (React.createElement(Modal, { header: lang.documents.preview, onToggle: () => setPreviewModalOpen(false) }, props.caseSequentialId && singleCase ? (React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement(RadioSelect, { active: previewPDF === PreviewPDF.Stævning, onClick: () => setPreviewPDF(PreviewPDF.Stævning), label: "St\u00E6vning" }),
                React.createElement(RadioSelect, { active: previewPDF === PreviewPDF.Kvittering, onClick: () => setPreviewPDF(PreviewPDF.Kvittering), label: "Kvittering" }),
                !!vouchers.filter((v) => v.appendix).length && (React.createElement(RadioSelect, { active: previewPDF === PreviewPDF.Posteringsbilag, onClick: () => setPreviewPDF(PreviewPDF.Posteringsbilag), label: "Posteringsbilag" }))),
            React.createElement("br", null),
            previewPDF === PreviewPDF.Stævning && (React.createElement(PDFTemplatePreview, { caseId: singleCase.caseId, templateId: '42f7bfc6-f373-44c9-8707-1282aca808d6', data: toPDFData(getData()) })),
            previewPDF === PreviewPDF.Kvittering && (React.createElement(PDFTemplatePreview, { caseId: singleCase.caseId, templateId: '94777afd-e545-4cbe-a384-394710852f45', data: toPDFData(getData()) })),
            previewPDF === PreviewPDF.Posteringsbilag &&
                vouchers
                    .filter((v) => v.appendix)
                    .map((v) => (React.createElement(ObjectComp, { key: v.id, type: "application/pdf", url: `${FILEBASE}/${v.appendix.fileId}/${v.appendix.fileName}` }))))) : (React.createElement("h3", { style: {
                margin: '4em 0',
                opacity: 0.7,
                textAlign: 'center',
            } }, lang.templates.selectTestCase))))));
};
export default Staevning;
//# sourceMappingURL=Staevning.js.map